import { Input } from "@components/";
import { useUserSearch } from "./useUserSearch";
import type { UserResult, SearchItem } from "./interfaces";

interface UserSearchProps {
  onSelect: (user: UserResult) => void;
  onClear?: () => void;
  placeholder?: string;
  initialValue?: string;
}

export function UserSearch({
  onSelect,
  onClear,
  placeholder = "Busque por nome ou email do solicitante",
  initialValue = "",
}: UserSearchProps) {
  const {
    query,
    results,
    open,
    loading,
    error,
    highlight,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleSelect,
    clearQuery,
  } = useUserSearch({
    onSelect: (item: SearchItem) => {
      onSelect(item as UserResult);
    },
    minChars: 2,
    debounceMs: 300,
    initialValue,
  });

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            label="Solicitante"
            placeholder={placeholder}
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoComplete="off"
          />

          {query && (
            <button
              type="button"
              onClick={() => {
                clearQuery();
                if (onClear) onClear();
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {open && loading && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-3">
          <div className="text-sm text-gray-500">Buscando usuários...</div>
        </div>
      )}

      {open && error && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-3">
          <div className="text-sm text-red-500">{error}</div>
        </div>
      )}

      {open && !loading && !error && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
          {results.map((item: SearchItem, index: number) => {
            const user = item as UserResult;
            return (
              <div
                key={item.id}
                className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  index === highlight ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
                onClick={() => {
                  console.log("🔧 DEBUG: Usuário clicado:", item);
                  handleSelect(item);
                }}
              >
                <div className="font-medium text-sm">{item.name}</div>
                {user.email && (
                  <div className="text-xs text-gray-500 mt-1">
                    Email: {user.email}
                  </div>
                )}
                {user.role && (
                  <div className="text-xs text-gray-500 mt-1">
                    Papel: {user.role}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open &&
        !loading &&
        !error &&
        results.length === 0 &&
        query.length >= 2 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
            <div className="text-sm text-gray-500">
              Nenhum usuário encontrado
            </div>
          </div>
        )}
    </div>
  );
}
